import { lazy, Suspense, useEffect, useState, type ReactElement } from "react";
import { createBrowserCartApi } from "../cart-api";
import {
  PRODUCT_CATALOG_PATH,
  PRODUCT_PAGE_PREFIX,
} from "../product-pages/product-pages-content";
import {
  createBrowserPublicCatalogApi,
  type PublicCatalogItem,
} from "../product-pages/public-catalog-api";
import {
  AUDIENCE_BANNERS,
  BENEFIT_ITEMS,
  BUSINESS_NAME,
  BUSINESS_NAME_LINE_1,
  BUSINESS_NAME_LINE_2,
  CATEGORIES,
  FOOTER_PRODUCT_LINKS,
  HOW_IT_WORKS_STEPS,
} from "./homepage-content";
import {
  buildBusinessContactLinks,
  formatPhoneForDisplay,
  loadBusinessContactSettings,
  VERIFIED_BUSINESS_SETTINGS,
  type BusinessContactSettings,
} from "./business-contact";
import { Icon } from "./homepage-icons";
import { ProductImage } from "./homepage-image";
import { PvcQuickOrder } from "./PvcQuickOrder";
import { SiteHeader } from "./SiteHeader";
import { TrustBar } from "./HomepageRefinements";
import { WORK_GALLERY_PATH } from "../work-gallery/work-gallery-api";
import { FeaturedProducts } from "./FeaturedProducts";
import { BusinessQuoteForm } from "./BusinessQuoteForm";
import { CustomerProof } from "./CustomerProof";
import { BrandMark } from "./BrandMark";
import { HomepageProductPhoto } from "./HomepageProductPhoto";
import { CONTACT_PATH } from "../public-routes";
import { DeferredHomepageSection } from "./DeferredHomepageSection";

const WorkGalleryShowcase = lazy(() =>
  import("../work-gallery/WorkGalleryShowcase").then((module) => ({
    default: module.WorkGalleryShowcase,
  })),
);

const GoogleReviewsSection = lazy(() =>
  import("../google-reviews/GoogleReviewsSection").then((module) => ({
    default: module.GoogleReviewsSection,
  })),
);

function publicHref(
  href: string,
  publicCatalog: PublicCatalogItem[] | null,
): string {
  if (publicCatalog == null || !href.startsWith(PRODUCT_PAGE_PREFIX)) return href;
  const slug = href.slice(PRODUCT_PAGE_PREFIX.length);
  return publicCatalog.some((item) => item.slug === slug)
    ? href
    : PRODUCT_CATALOG_PATH;
}

function HeroSection() {
  return (
    <section className="hp-hero" aria-label="חלק ראשי">
      <img
        className="hp-hero-photo"
        src="/images/homepage-2026/hero.webp"
        alt="שלט PVC, ניירת עסקית, מדבקות וחולצה מודפסת בסטודיו מואר"
        width={1672}
        height={941}
        fetchPriority="high"
      />
      <div className="hp-section hp-hero-inner">
        <div className="hp-hero-copy">
          <span className="hp-eyebrow">{BUSINESS_NAME}</span>
          <h1>
            דפוס ושילוט
            <br /> בהתאמה אישית.
          </h1>
          <p className="hp-hero-promise">עושים רושם. עושים שמח.</p>
          <p>
            לעסקים, למוסדות ולרגעים שלכם.
            <br />
            בוחרים מוצר, מוסיפים את העיצוב שלכם ומתחילים ליצור.
          </p>
          <div className="hp-hero-ctas">
            <a className="hp-btn hp-btn-primary" href="#quick-order">
              מתחילים להזמין <span aria-hidden="true">←</span>
            </a>
            <a className="hp-btn hp-btn-secondary" href="#business-quote">
              צריכים עזרה? <Icon name="service" size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function AudienceShortcuts({ publicCatalog }: { publicCatalog: PublicCatalogItem[] | null }) {
  const eventHref = publicHref(`${PRODUCT_PAGE_PREFIX}event-invitations`, publicCatalog);
  return (
    <section
      className="hp-audience-choice"
      aria-labelledby="hp-audience-choice-title"
    >
      <div className="hp-section">
        <div className="hp-audience-choice-heading">
          <span className="hp-eyebrow">בחרו מסלול</span>
          <h2 id="hp-audience-choice-title">איך נוכל לעזור לכם היום?</h2>
          <p>שני מסלולים ברורים כדי להגיע מהר למוצרים, לתמחור או להצעת מחיר.</p>
        </div>
        <nav className="hp-audience-shortcuts" aria-label="בחירת מסלול הזמנה">
          <a
            className="hp-audience-shortcut hp-audience-shortcut--business"
            href="#business-quote"
          >
            <span className="hp-audience-shortcut-icon">
              <Icon name="business" size={38} />
            </span>
            <span>
              <strong>לעסק / מוסד</strong>
              <small>שילוט, מיתוג, כמות והצעת מחיר מסודרת</small>
            </span>
            <span className="hp-audience-shortcut-cta" aria-hidden="true">
              למוצרים עסקיים ←
            </span>
          </a>
          <a
            className="hp-audience-shortcut hp-audience-shortcut--event"
            href={eventHref}
          >
            <span className="hp-audience-shortcut-icon">
              <Icon name="gifts" size={38} />
            </span>
            <span>
              <strong>לאירוע פרטי</strong>
              <small>שלטים, הזמנות, מדבקות ומתנות אישיות</small>
            </span>
            <span className="hp-audience-shortcut-cta" aria-hidden="true">
              {eventHref === PRODUCT_CATALOG_PATH ? "לקטלוג המוצרים ←" : "להזמנות לאירוע ←"}
            </span>
          </a>
        </nav>
      </div>
    </section>
  );
}

function CategoriesSection({
  publicCatalog,
}: {
  publicCatalog: PublicCatalogItem[] | null;
}) {
  return (
    <section
      className="hp-section hp-categories-section"
      id="categories"
      aria-labelledby="hp-categories-title"
    >
      <div className="hp-section-heading">
        <div>
          <span className="hp-eyebrow">קטגוריות מרכזיות</span>
          <h2 id="hp-categories-title">מה תרצו ליצור היום?</h2>
        </div>
        <a className="hp-section-action" href={PRODUCT_CATALOG_PATH}>
          כל הקטגוריות <span aria-hidden="true">←</span>
        </a>
      </div>
      <div className="hp-categories-grid">
        {CATEGORIES.map((category) => (
          <a
            key={category.id}
            id={category.id}
            className="hp-category-card"
            href={publicHref(category.href, publicCatalog)}
          >
            <HomepageProductPhoto
              visual={category.visual}
              alt={category.title}
              className="hp-category-photo"
            />
            <span className="hp-category-copy">
              <span className="hp-category-icon" aria-hidden="true">
                <Icon name={category.icon} size={24} />
              </span>
              <h3>{category.title}</h3>
              <small>{category.subtitle}</small>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function AudienceBanners({
  publicCatalog,
}: {
  publicCatalog: PublicCatalogItem[] | null;
}) {
  return (
    <section className="hp-section hp-audience-section" aria-label="קהלי יעד">
      <div className="hp-audience-grid">
        {AUDIENCE_BANNERS.map((banner) => (
          <article
            key={banner.id}
            className={`hp-audience-banner hp-audience-banner--${banner.audience}`}
          >
            <ProductImage
              className="hp-audience-art"
              src={
                banner.audience === "business"
                  ? "/images/banners/business-current.avif"
                  : "/images/occasions/celebration-table.jpg"
              }
              alt=""
              width={1600}
              height={700}
              loading="lazy"
              fill
              fallback={null}
            />
            <div className="hp-audience-copy">
              <h3>{banner.title.replace(/\u00a0/g, " ")}</h3>
              <p>{banner.copy}</p>
              <a className="hp-btn hp-btn-primary" href={publicHref(banner.href, publicCatalog)}>
                {banner.ctaLabel} <span aria-hidden="true">←</span>
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="hp-how-it-works" aria-labelledby="hp-how-title">
      <div className="hp-section">
        <div className="hp-section-heading">
          <h2 id="hp-how-title">איך זה עובד?</h2>
          <span>מהרעיון ועד לקבלת המוצר</span>
        </div>
        <ol className="hp-steps">
          {HOW_IT_WORKS_STEPS.map((step) => (
            <li key={step.step}>
              <span className="hp-step-number">{step.step}</span>
              <span className="hp-step-icon" aria-hidden="true">
                <Icon name={step.icon} size={27} />
              </span>
              <span>{step.label}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function BenefitsSection() {
  return (
    <section
      className="hp-section hp-benefits"
      aria-labelledby="hp-benefits-title"
    >
      <h2 id="hp-benefits-title">למה לבחור בנו?</h2>
      <div className="hp-benefits-grid">
        {BENEFIT_ITEMS.map((item) => (
          <div key={item.id}>
            <span className="hp-benefit-icon" aria-hidden="true">
              <Icon name={item.icon} size={28} />
            </span>
            <span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function SiteFooter({
  businessSettings,
  publicCatalog,
}: {
  businessSettings: BusinessContactSettings;
  publicCatalog: PublicCatalogItem[] | null;
}) {
  const links = buildBusinessContactLinks(businessSettings);
  return (
    <footer className="hp-footer">
      <div className="hp-section hp-footer-grid">
        <div className="hp-footer-brand">
          <BrandMark />
          <strong>
            {BUSINESS_NAME_LINE_1}
            <br />
            {BUSINESS_NAME_LINE_2}
          </strong>
          <p>יוצרים יחד דברים טובים.</p>
        </div>
        <nav className="hp-footer-col" aria-label="מוצרים">
          <h3>מוצרים</h3>
          {FOOTER_PRODUCT_LINKS.map((link) => (
            <a key={link.id} href={publicHref(link.href, publicCatalog)}>
              {link.label}
            </a>
          ))}
        </nav>
        <nav className="hp-footer-col" aria-label="שירות לקוחות">
          <h3>שירות לקוחות</h3>
          <a href="/account">אזור אישי</a>
          <a href="/account#orders">מעקב הזמנה</a>
          <a href={WORK_GALLERY_PATH}>העבודות שלנו</a>
          <a href={CONTACT_PATH}>צור קשר</a>
          <a href="/privacy">מדיניות פרטיות</a>
          <a href="/accessibility">הצהרת נגישות</a>
        </nav>
        <div className="hp-footer-col hp-footer-contact" aria-label="צרו קשר">
          <h3>דברו איתנו</h3>
          {links.phoneHref ? (
            <a href={links.phoneHref}>
              <bdi>{formatPhoneForDisplay(businessSettings.phone)}</bdi>
            </a>
          ) : null}
          {links.emailHref ? (
            <a href={links.emailHref}>
              <bdi>{businessSettings.email}</bdi>
            </a>
          ) : null}
          {links.whatsappHref ? (
            <a
              href={links.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </a>
          ) : null}
          <span>{businessSettings.addressText}</span>
          <span className="hp-footer-hours">
            {businessSettings.openingHoursText}
          </span>
        </div>
      </div>
      <div className="hp-section hp-footer-bottom">
        © {new Date().getFullYear()} {BUSINESS_NAME}. כל הזכויות שמורות.
      </div>
    </footer>
  );
}

export function HomePage(): ReactElement {
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [businessSettings, setBusinessSettings] =
    useState<BusinessContactSettings>(VERIFIED_BUSINESS_SETTINGS);
  const [publicCatalog, setPublicCatalog] = useState<PublicCatalogItem[] | null>(null);
  useEffect(() => {
    let active = true;
    void createBrowserCartApi()
      .loadSummary()
      .then((cart) => {
        if (active) setTotalQuantity(cart?.totalQuantity ?? 0);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    void createBrowserPublicCatalogApi()
      .load(controller.signal)
      .then((items) => {
        // An empty server-managed catalog is not a removal of the curated
        // quote/request product pages. Keep brochure links and search usable.
        if (!controller.signal.aborted) setPublicCatalog(items.length ? items : null);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadBusinessContactSettings(controller.signal)
      .then((settings) => {
        if (!controller.signal.aborted) setBusinessSettings(settings);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);
  return (
    <div className="hp-shell" dir="rtl" lang="he">
      <a className="hp-skip-link" href="#hp-main">
        דלג לתוכן הראשי
      </a>
      <SiteHeader
        totalQuantity={totalQuantity}
        businessSettings={businessSettings}
        publicProductSlugs={publicCatalog?.map((item) => item.slug) ?? null}
      />
      <main id="hp-main">
        <HeroSection />
        <AudienceShortcuts publicCatalog={publicCatalog} />
        <TrustBar businessSettings={businessSettings} />
        <CategoriesSection publicCatalog={publicCatalog} />
        <PvcQuickOrder />
        <FeaturedProducts publicCatalog={publicCatalog} />
        <AudienceBanners publicCatalog={publicCatalog} />
        <DeferredHomepageSection>
          <Suspense fallback={null}>
            <WorkGalleryShowcase />
          </Suspense>
        </DeferredHomepageSection>
        <CustomerProof />
        <HowItWorks />
        <BenefitsSection />
        <DeferredHomepageSection>
          <Suspense fallback={null}>
            <GoogleReviewsSection />
          </Suspense>
        </DeferredHomepageSection>
        <BusinessQuoteForm
          businessSettings={businessSettings}
          requestedProduct={null}
        />
      </main>
      <SiteFooter businessSettings={businessSettings} publicCatalog={publicCatalog} />
    </div>
  );
}
